import { Controller } from "@hotwired/stimulus"

// Stripe Elements + payment confirmation for job transaction purchases.
// Mount only while the purchase panel is open and the charge UI is visible so
// Turbo page-cache snapshots never serialize a live Stripe iframe.
export default class extends Controller {
  static values = {
    url: String,
    publishableKey: String,
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.boundStripeSubmit = this.handleStripeSubmit.bind(this)
    this.boundChargeToggled = this.onChargeToggled.bind(this)
    this.boundPanelOpened = this.onPanelOpened.bind(this)
    this.boundPanelClosed = this.onPanelClosed.bind(this)
    this.boundCardChanged = this.onCardChanged.bind(this)
    this.boundBeforeCache = this.onBeforeCache.bind(this)
    this.boundTurboRender = this.onTurboRender.bind(this)

    this.stripeClient = null
    this.stripeMounted = false
    this.mounting = false
    this.stripeElements = null
    this.stripeCard = null

    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    document.addEventListener("turbo:render", this.boundTurboRender)
    document.addEventListener("job-transactions-panel:opened", this.boundPanelOpened)
    document.addEventListener("job-transactions-panel:closed", this.boundPanelClosed)
    document.addEventListener("card-selector:changed", this.boundCardChanged)
    this.element.addEventListener("purchase-form:chargeToggled", this.boundChargeToggled)

    // Always intercept submits, regardless of whether the Stripe Element is
    // mounted. Every submit goes through the payments endpoint first (server
    // validates the order and decides whether payment is due), so the form
    // can never POST a charge order without a confirmed PaymentIntent — even
    // if the panel lifecycle left the Element unmounted.
    this.element.addEventListener("submit", this.boundStripeSubmit)

    if (document.getElementById("vendors-stripe-elements")) {
      void this.handleRedirectIfNeeded()
      this.scheduleSyncMount()
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    document.removeEventListener("turbo:render", this.boundTurboRender)
    document.removeEventListener("job-transactions-panel:opened", this.boundPanelOpened)
    document.removeEventListener("job-transactions-panel:closed", this.boundPanelClosed)
    document.removeEventListener("card-selector:changed", this.boundCardChanged)
    this.element.removeEventListener("purchase-form:chargeToggled", this.boundChargeToggled)
    this.element.removeEventListener("submit", this.boundStripeSubmit)

    this.destroyStripe()
  }

  onBeforeCache() {
    this.destroyStripe()
  }

  onTurboRender(event) {
    if (event.detail?.render?.method !== "restore") return

    this.scheduleSyncMount()
  }

  onPanelOpened() {
    this.scheduleSyncMount()
  }

  onPanelClosed() {
    this.destroyStripe()
  }

  onChargeToggled(event) {
    if (event.detail.charge) {
      this.scheduleSyncMount()
    } else {
      this.destroyStripe()
    }
  }

  onCardChanged() {
    this.scheduleSyncMount()
  }

  scheduleSyncMount() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void this.syncStripeMount()
      })
    })
  }

  async syncStripeMount() {
    if (this.shouldMountStripe()) {
      await this.mountStripe()
    } else if (this.stripeMounted) {
      this.destroyStripe()
    }
  }

  ensureStripeClient() {
    if (typeof window.Stripe === "undefined") return null

    if (!this.stripeClient) {
      this.stripeClient = window.Stripe(this.publishableKeyValue)
    }

    return this.stripeClient
  }

  shouldMountStripe() {
    const mountEl = document.getElementById("vendors-stripe-elements")
    if (!mountEl || !this.ensureStripeClient()) return false
    if (!this.isPanelOpen()) return false
    if (!this.isChargeSectionVisible()) return false
    if (!this.isStripeContainerVisible()) return false

    return true
  }

  isPanelOpen() {
    const panel = document.getElementById("pages-job_transactions-new-panel")
    return panel && !panel.classList.contains("translate-x-full")
  }

  isChargeSectionVisible() {
    const chargeSection = document.getElementById("job_transaction_delivery_category_charge")
    return chargeSection && !chargeSection.classList.contains("hidden")
  }

  isStripeContainerVisible() {
    const mountEl = document.getElementById("vendors-stripe-elements")
    const wrapper = mountEl?.parentElement

    return wrapper && !wrapper.classList.contains("hidden")
  }

  async mountStripe() {
    if (this.mounting || !this.shouldMountStripe()) return

    const stripeElementsEl = document.getElementById("vendors-stripe-elements")
    const stripeForm = stripeElementsEl?.closest("form")
    const stripeClient = this.ensureStripeClient()
    if (!stripeElementsEl || !stripeForm || !stripeClient) return

    if (this.stripeMounted && stripeElementsEl.childElementCount > 0) return

    if (this.stripeCard) {
      this.destroyStripe()
    }

    this.mounting = true

    try {
      this.clearStripeContainer()

      this.stripeElements = stripeClient.elements({
        amount: 10000,
        currency: "usd",
        mode: "payment",
        // Must match the PaymentIntent's capture_method (manual — the server
        // captures after the transaction records are committed), or
        // confirmPayment rejects the mismatch.
        captureMethod: "manual",
      })
      this.stripeCard = this.stripeElements.create("payment")

      await this.stripeCard.mount(`#${stripeElementsEl.id}`)
      this.stripeMounted = true
    } catch (error) {
      console.error("Stripe Payment Element failed to mount:", error)
      this.destroyStripe()
    } finally {
      this.mounting = false
    }
  }

  destroyStripe() {
    if (this.stripeCard) {
      this.stripeCard.unmount()
      this.stripeCard.destroy()
      this.stripeCard = null
    }

    this.stripeElements = null
    this.stripeClient = null
    this.stripeMounted = false
    this.clearStripeContainer()
  }

  clearStripeContainer() {
    document.getElementById("vendors-stripe-elements")?.replaceChildren()
  }

  async handleRedirectIfNeeded() {
    const stripeElementsEl = document.getElementById("vendors-stripe-elements")
    const stripeForm = stripeElementsEl?.closest("form")
    const stripeClient = this.ensureStripeClient()
    if (!stripeForm || !stripeClient) return

    await this.handleStripeRedirectReturn(stripeForm, stripeClient)
  }

  async handleStripeRedirectReturn(stripeForm, stripeClient) {
    const returnParams = new URLSearchParams(window.location.search)
    const redirectClientSecret = returnParams.get("payment_intent_client_secret")
    const redirectStatus = returnParams.get("redirect_status")

    if (
      !redirectClientSecret ||
      redirectStatus !== "succeeded" ||
      stripeForm.querySelector('input[name="stripeToken"]')
    ) {
      return false
    }

    const { paymentIntent, error: retrieveError } =
      await stripeClient.retrievePaymentIntent(redirectClientSecret)

    // Manual capture: after a 3DS redirect the intent is only authorized
    // (requires_capture); the server captures it once the transaction records
    // are committed. "succeeded" covers intents created before manual capture
    // was introduced.
    if (
      retrieveError ||
      !paymentIntent ||
      (paymentIntent.status !== "succeeded" &&
        paymentIntent.status !== "requires_capture")
    ) {
      return false
    }

    const url = new URL(window.location.href)
    url.searchParams.delete("payment_intent")
    url.searchParams.delete("payment_intent_client_secret")
    url.searchParams.delete("redirect_status")
    window.history.replaceState({}, "", url.pathname + url.search + url.hash)

    this.appendStripeTokenAndSubmit(stripeForm, paymentIntent.id)
    return true
  }

  async handleStripeSubmit(event) {
    event.preventDefault()

    const submitButton = this.element.querySelector('button[type="submit"]')
    if (submitButton) submitButton.disabled = true

    const newCardElement = document.querySelector(
      "#job_transaction_selected_payment_method_-1"
    )
    const newCard = !newCardElement || newCardElement.checked

    if (newCard && this.stripeElements) {
      const { error: submitError } = await this.stripeElements.submit()
      if (submitError) {
        this.handleStripeError(submitError)
        return
      }
    }

    // Send the whole form so the server can validate the order BEFORE
    // creating the PaymentIntent (e.g. print orders without a shipping
    // address are rejected while the card is still untouched). File inputs
    // are stripped: uploads are referenced by signed-id hidden fields, and
    // raw files have no place in this request.
    const formData = new FormData(this.element)
    Array.from(formData.entries()).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.delete(key)
      }
    })

    // Validate the order and obtain the PaymentIntent clientSecret.
    const response = await fetch(this.urlValue, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
      method: "POST",
      body: formData,
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      this.handleStripeError({
        message:
          data.error ||
          "There was a problem processing your order. Please try again.",
      })
      return
    }
    const clientSecret = data.clientSecret

    // No charge categories selected (e.g. deposit-only order): nothing to
    // pay through Stripe, submit the form directly. form.submit() bypasses
    // this listener, so the validated order posts natively.
    if (!clientSecret) {
      this.element.submit()
      return
    }

    // Payment is due but the Payment Element is not mounted (e.g. the panel
    // lifecycle unmounted it). Never fall through to a native submit: surface
    // an error and try to remount so the user can retry.
    const stripeClient = this.ensureStripeClient()
    if (!stripeClient || (newCard && !this.stripeElements)) {
      this.handleStripeError({
        message:
          "The payment form is not ready. Please wait a moment and try again.",
      })
      this.scheduleSyncMount()
      return
    }

    let error
    let paymentIntent

    if (newCard) {
      ;({ error, paymentIntent } = await stripeClient.confirmPayment({
        clientSecret,
        elements: this.stripeElements,
        confirmParams: {
          return_url: this.stripeReturnUrl(),
        },
        redirect: "if_required",
      }))
    } else {
      const selectedRadioButton = document.querySelector(
        '[name="job_transaction[selected_payment_method]"]:checked'
      )
      const firstPaymentMethod = document.querySelector(
        '[name="job_transaction[selected_payment_method]"]'
      )
      const paymentMethodId =
        (selectedRadioButton && selectedRadioButton.value) ||
        (firstPaymentMethod && firstPaymentMethod.value)

      ;({ error, paymentIntent } = await stripeClient.confirmPayment({
        clientSecret,
        confirmParams: {
          payment_method: paymentMethodId,
          return_url: this.stripeReturnUrl(),
        },
        redirect: "if_required",
      }))
    }

    if (error) {
      this.handleStripeError(error)
    } else if (
      paymentIntent &&
      // Manual capture: confirmPayment only authorizes the card
      // (requires_capture); the server captures after the transaction records
      // are committed. "succeeded" covers intents created before manual
      // capture was introduced.
      (paymentIntent.status === "succeeded" ||
        paymentIntent.status === "requires_capture")
    ) {
      this.hideStripeErrors()
      this.appendStripeTokenAndSubmit(this.element, paymentIntent.id)
    } else {
      this.handleStripeError({
        message: "There was a problem processing your payment. Please try again.",
      })
    }
  }

  handleStripeError(error) {
    const submitButton = this.element.querySelector('button[type="submit"]')
    if (submitButton) {
      submitButton.disabled = false
    }

    const cardErrors = document.getElementById("card-selector-errors")
    const stripeElementsErrors = document.getElementById(
      "vendors-stripe-elements-errors"
    )

    if (cardErrors) {
      cardErrors.textContent =
        "There was an error processing your payment. Please try again."
      cardErrors.classList.remove("hidden")
    }

    if (stripeElementsErrors) {
      stripeElementsErrors.textContent = error.message
      stripeElementsErrors.classList.remove("hidden")
    }
  }

  hideStripeErrors() {
    document.getElementById("card-selector-errors")?.classList.add("hidden")
    document.getElementById("vendors-stripe-elements-errors")?.classList.add("hidden")
  }

  appendStripeTokenAndSubmit(form, tokenId) {
    const stripeHiddenInput = document.createElement("input")
    stripeHiddenInput.setAttribute("type", "hidden")
    stripeHiddenInput.setAttribute("name", "stripeToken")
    stripeHiddenInput.setAttribute("value", tokenId)
    form.appendChild(stripeHiddenInput)
    form.submit()
  }

  stripeReturnUrl() {
    return window.location.href.split("#")[0]
  }
}
